import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getStReportPageImage } from "../../utils/utility-functions";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import {
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  MODEL_TYPE_SOCIOPATH,
  PERSONALITY_TEST_ASSESSMENT_ID,
  SOCIOPATH_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import PathHelper from "../../utils/path-helper";
import Header from "../../components/header";
import Footer from "../../components/footer";
import Loader from "../../components/loader";
import BackToTopButton from "../../components/back-to-top";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import { CallToActionSection } from "../../components/call-to-action/component";
import InviteFriend from "../../components/invite-friend";
import ReportWrapper from "../../components/report-wrapper";
import ExpandableSectionV2 from "../../components/expandable-collapsible-v2/";

import { mapSociopathReportModel, unwrapSociopathContent } from "./report-model";
import ReportDebugPanel, {
  EMPTY_DEV_OVERRIDES,
  DEV_PRESETS,
  applyDevOverrides,
  isDevPreviewAllowed,
} from "./dev-tools/report-debug-panel";
import {
  getFactorVisualStyle,
  getSeverityGaugeVisualStyle,
  getSeverityBandColors,
} from "./report-visual-style";
import { renderMethodologyText } from "./report-explanation-text";

// Not reusing CONTENT_KEY_BIG_FIVE despite the same "ALL_CONTENT" value —
// that name would be misleading here. Only modelType (MODEL_TYPE_SOCIOPATH,
// general-config.js) varies by locale; contentKey is constant.
const SOCIOPATH_CONTENT_KEY = "ALL_CONTENT";

// ---------------------------------------------------------------------------
// Small local presentation components — everything below renders only from
// the normalized `reportData` shape (see report-model.js).
// ---------------------------------------------------------------------------

const BANNER_COLORS = {
  header: { from: "#26307f", to: "#3f63d8" },
  explanation: { from: "#6a23c9", to: "#b24bd6" },
  insights: { from: "#c0392b", to: "#e0685a" },
};

const dotTextureGradient = ({ from, to }) =>
  `radial-gradient(1.6px 1.6px at 8% 30%, rgba(255,255,255,.4), transparent),` +
  `radial-gradient(1.4px 1.4px at 35% 75%, rgba(255,255,255,.28), transparent),` +
  `radial-gradient(1.1px 1.1px at 65% 20%, rgba(255,255,255,.3), transparent),` +
  `radial-gradient(1.5px 1.5px at 85% 65%, rgba(255,255,255,.25), transparent),` +
  `radial-gradient(1px 1px at 95% 15%, rgba(255,255,255,.3), transparent),` +
  `linear-gradient(105deg, ${from}, ${to})`;

// Flexible title column + fixed, non-shrinking spacer column (flex, not
// blunt block-level padding) so the reserved width for ExpandableSectionV2's
// absolutely positioned collapse button (w-10 at right-4) only costs as much
// horizontal space as the button's actual footprint needs, instead of a
// pr-* that shrinks every wrapped line uniformly and over-reserves. The
// spacer is a plain empty element, not the real control — the actual
// clickable toggle still lives entirely in ExpandableSectionV2, untouched.
// Mobile-first type scale (text-xl -> text-2xl -> text-3xl) trades a little
// banner size for meaningfully more characters per line at 390px.
//
// Mobile side padding (px-3) intentionally matches ExpandableSectionV2's own
// body padding (p-3) so the title's left/right text edges line up with the
// body paragraph's below it — mismatched insets are what make a banner read
// as its own separately-framed box nested inside the card, not available
// width. sm:px-8 (tablet/desktop) is unchanged, so this is mobile-only.
const SectionBanner = ({ title, colors }) => (
  <div
    className="relative overflow-hidden px-3 sm:px-8 py-6"
    style={{ backgroundImage: dotTextureGradient(colors) }}
  >
    <div className="relative z-10 flex items-start gap-2">
      <h2 className="min-w-0 flex-1 text-xl sm:text-2xl lg:text-3xl font-bold text-white break-words">
        {title}
      </h2>
      {/* w-12 (48px), not w-9: the collapse toggle (ExpandableSectionV2,
          w-10 at right-4) needs total clearance >= 56px from the section's
          right edge. This spacer's width is paired with the banner's own
          side padding above — px-3 (12px) + 48px = 60px, matching the
          original px-6 (24px) + 36px = 60px this replaced, so shrinking the
          mobile padding for edge alignment didn't silently shrink the
          toggle's clearance too. */}
      <span className="w-12 sm:w-11 flex-shrink-0" aria-hidden="true" />
    </div>
  </div>
);

// Semicircular gauge — radius must equal half the chord between the arc's
// start/end x-coordinates, or the curve renders lopsided.
const OverallResultGauge = ({ score, maxScore, label, sublabel, visualStyle }) => {
  const { t } = useTranslation();
  const radius = 120;
  const circumference = Math.PI * radius;
  const percentage = Math.min(Math.max(score / maxScore, 0), 1);
  const dashOffset = circumference * (1 - percentage);

  return (
    <div className="flex flex-col items-center w-full max-w-[360px] mx-auto">
      <div className="relative w-full">
        <svg viewBox="0 0 300 185" className="w-full">
          <defs>
            <linearGradient id="sociopathGaugeGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor={visualStyle.gradientFrom} />
              <stop offset="1" stopColor={visualStyle.gradientTo} />
            </linearGradient>
          </defs>
          <path
            d="M30 150 A120 120 0 0 1 270 150"
            fill="none"
            stroke="#EEF0F8"
            strokeWidth="20"
            strokeLinecap="round"
          />
          <path
            d="M30 150 A120 120 0 0 1 270 150"
            fill="none"
            stroke="url(#sociopathGaugeGradient)"
            strokeWidth="20"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset .7s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pt-8">
          <div
            className="text-[32px] font-extrabold leading-none"
            style={{ color: visualStyle.textColor }}
          >
            {label}
          </div>
          <div className="mt-1 text-sm font-semibold text-gray-500">
            {sublabel}
          </div>
        </div>
        <div className="absolute bottom-2 left-6 right-6 flex justify-between text-gray-400 text-xs font-bold">
          <span>{t("report.sociopath.low")}</span>
          <span>{t("report.sociopath.high")}</span>
        </div>
      </div>
    </div>
  );
};

// Adapted from the IQ report's SectionScoreItem/ScoreBar pattern (dot + name
// + compact score/band + mini bar) — but colored via getFactorVisualStyle,
// the same per-factor hue FactorCard already uses below, so a trait's color
// identity here always matches its own FactorCard in Section 2 rather than
// inventing a new palette. Reads directly from reportData.factors (already
// separate title/percentage/levelLabel fields, not a pre-formatted string)
// so name/score/band can be styled independently without touching
// report-model.js. flex-wrap lets the score/band group drop to its own line
// instead of compressing or truncating a long localized trait name or band
// label — no truncate/nowrap anywhere in this row.
const TraitSummaryRow = ({ title, percentage, visualMaxScore, levelLabel, color }) => {
  const visualStyle = getFactorVisualStyle(color, percentage);
  return (
    <div className="py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: visualStyle.barColor }}
            aria-hidden="true"
          />
          <span className="font-semibold text-gray-800 break-words">{title}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-sm font-bold text-gray-900">{percentage}</span>
          <span className="text-sm text-gray-400">/{visualMaxScore}</span>
          <span
            className="font-semibold rounded-full px-2 py-0.5 text-xs whitespace-normal"
            style={{ color: visualStyle.textColor, backgroundColor: visualStyle.bgColor }}
          >
            {levelLabel}
          </span>
        </div>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${percentage}%`, backgroundColor: visualStyle.barColor }}
        />
      </div>
    </div>
  );
};

const TraitSummaryList = ({ factors }) => (
  <div className="w-full">
    {factors.map((factor) => (
      <TraitSummaryRow key={factor.key} {...factor} />
    ))}
  </div>
);

// Local hierarchy: factor name (strong) -> score/band (compact, split into
// two pieces per Pass 2's trait-row precedent rather than one dense pill,
// but laid out inline in the header instead of a second dot+bar row, so it
// doesn't just repeat the summary rows' UI) -> body (readable, regular
// weight). `body` is a single backend-supplied paragraph (factor?.bands?.
// [band]?.description) — there is no separate short-interpretation field to
// split out, so no such split is fabricated here; only typography/spacing
// changed, not content structure.
const FactorCard = ({ title, percentage, visualMaxScore, levelLabel, color, body }) => {
  const visualStyle = getFactorVisualStyle(color, percentage);

  return (
    <div
      className="rounded-2xl p-4 sm:p-5 lg:p-6 flex flex-col gap-3"
      style={{ backgroundColor: visualStyle.bgColor }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h4
          className="font-bold text-base min-w-0 break-words"
          style={{ color: visualStyle.textColor }}
        >
          {title}
        </h4>
        <div className="flex-shrink-0 flex flex-wrap items-center gap-1.5">
          <span className="font-bold text-sm" style={{ color: visualStyle.textColor }}>
            {percentage}/{visualMaxScore}
          </span>
          <span
            className="font-medium text-xs bg-white/70 rounded-full px-2.5 py-1"
            style={{ color: visualStyle.textColor }}
          >
            {levelLabel}
          </span>
        </div>
      </div>
      <div className="h-2 rounded-full bg-white/70 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${percentage}%`, backgroundColor: visualStyle.barColor }}
        />
      </div>
      <p className="text-[15px] sm:text-base font-normal text-gray-600 leading-relaxed">
        {body}
      </p>
    </div>
  );
};

const ExplanationCallout = ({ label, children }) => (
  <div className="bg-indigo-50 border-l-4 border-indigo-500 rounded-lg px-5 py-4 mb-6">
    <p className="text-[15px] sm:text-base font-normal leading-relaxed text-gray-600 text-left">
      <strong className="text-indigo-700">{label}</strong> {children}
    </p>
  </div>
);

const IconListCard = ({ heading, items }) => (
  <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 sm:p-5 lg:p-6">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
        →
      </div>
      <h4 className="font-bold text-base text-indigo-700">{heading}</h4>
    </div>
    <ul className="list-disc pl-6 space-y-2">
      {items.map((item, index) => (
        <li key={index} className="text-sm text-gray-700 leading-relaxed">
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const NOTICE_COLORS = {
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800" },
  emerald: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-800",
  },
};

const NoticeCard = ({ color, heading, body }) => {
  const colors = NOTICE_COLORS[color] || NOTICE_COLORS.amber;
  return (
    <div className={`${colors.bg} border ${colors.border} rounded-2xl p-4 sm:p-5 lg:p-6`}>
      <h4 className={`font-bold text-base mb-2 ${colors.text}`}>{heading}</h4>
      <p className="text-[15px] sm:text-base font-normal text-gray-600 leading-relaxed">
        {body}
      </p>
    </div>
  );
};

const SociopathTestReport = ({
  funnelStep,
  getUserByIdConnect,
  getInsightfulContentConnect,
  getAssessmentResultByAssessmentAttemptIdConnect,
  sendToFriendReportOnEmailConnect,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getTransactionDetailConnect,
  getOrderDataByUserIdConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
  const userId = queryParams.get("uid") || userGUID;
  const assessmentAttemptId = queryParams.get("aai");
  const [userData, setUserData] = useState(null);
  const [insightfulContent, setInsightfulContent] = useState(null);
  const [assessmentResult, setAssessmentResult] = useState(null);
  // Three independent flags — each async flow only ever touches its own, so
  // one finishing early can never mask another still being in flight.
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [isResultLoading, setIsResultLoading] = useState(true);
  const isLoading = isUserLoading || isContentLoading || isResultLoading;
  const [devOverrides, setDevOverrides] = useState(EMPTY_DEV_OVERRIDES);
  const devPreviewAllowed = isDevPreviewAllowed(queryParams);

  const result1Image = getStReportPageImage("st-result-1");
  const result2Image = getStReportPageImage("st-result-2");

  useEffect(() => {
    if (userId && isAuth) {
      setIsUserLoading(true);
      getUserByIdConnect(userId)
        .then((res) => {
          if (res) setUserData(res);
        })
        .finally(() => setIsUserLoading(false));
    }
  }, [userId, isAuth]);

  // Locale-keyed content fetch (MODEL_TYPE_SOCIOPATH, general-config.js).
  // Keyed on currentLocale (from useParams()), so it refetches real
  // translated content whenever the route locale changes — including from
  // a Dev Controls language switch, which navigates to a new locale URL.
  // Falls back to English if currentLocale has no matching model type.
  useEffect(() => {
    getInsightfulContentConnect({
      modelType: MODEL_TYPE_SOCIOPATH[currentLocale] || MODEL_TYPE_SOCIOPATH[LOCALE.EN],
      contentKey: SOCIOPATH_CONTENT_KEY,
    })
      .then((res) => {
        if (res) setInsightfulContent(res);
      })
      .catch((error) => console.error("Error fetching content:", error))
      .finally(() => setIsContentLoading(false));
  }, [currentLocale]);

  // Result fetch: GET /api/v1/assessment/{aai}/result. userTraits[].trait.title
  // ("boldness"/"meanness"/"disinhibition") and userTraits[].score/percentage
  // are real; there is no overall field on this response — see report-model.js
  // for how the overall gauge value is derived without one.
  useEffect(() => {
    if (!assessmentAttemptId) {
      setIsResultLoading(false);
      return;
    }
    getAssessmentResultByAssessmentAttemptIdConnect(assessmentAttemptId, currentLocale)
      .then((res) => {
        if (res) setAssessmentResult(res);
      })
      .catch((error) => console.error("Error fetching assessment result:", error))
      .finally(() => setIsResultLoading(false));
  }, [assessmentAttemptId, currentLocale]);

  // Report-local scroll reset (pattern shared with child-autism/component.js).
  // isLoading starts true, so this only meaningfully fires on the true ->
  // false transition once real content has rendered — never from Dev
  // Controls, which don't touch these loading flags.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  // Both fetches must resolve before mapping — mapSociopathReportModel must
  // never be called with a null value. The isLoading gate below stops the
  // JSX that reads `reportData`, but this assignment still runs every render.
  const baseReportData =
    insightfulContent && assessmentResult
      ? mapSociopathReportModel(assessmentResult, insightfulContent)
      : null;

  // Dev-preview-only layer (dev-tools/report-debug-panel.js) — gated by
  // devPreviewAllowed, never touches the API, no effect on production data.
  const reportData =
    devPreviewAllowed && baseReportData
      ? applyDevOverrides(
          baseReportData,
          devOverrides,
          unwrapSociopathContent(insightfulContent)
        )
      : baseReportData;

  const handleDevFactorChange = (key, percentage) =>
    setDevOverrides((prev) => ({
      ...prev,
      factors: { ...prev.factors, [key]: { percentage } },
    }));

  const handleDevOverallChange = (score) =>
    setDevOverrides((prev) => ({ ...prev, overall: { score } }));

  const handleDevPreset = (name) => {
    const preset = DEV_PRESETS[name];
    setDevOverrides({
      factors: {
        boldness: { percentage: preset.boldness },
        meanness: { percentage: preset.meanness },
        disinhibition: { percentage: preset.disinhibition },
      },
      overall: { score: preset.overall },
    });
  };

  const handleDevReset = () => {
    setDevOverrides(EMPTY_DEV_OVERRIDES);
  };

  // Dev Controls language switcher — uses a full browser navigation
  // (window.location.assign), not React Router's navigate(). i18nextInit.js
  // only detects the locale from window.location.pathname once, at app
  // bootstrap (src/index.js); an SPA navigate() would update the route and
  // refetch content but never rerun that detection, leaving i18n.language
  // (and everything driven by it — footer, shared UI) stuck on the original
  // locale. A full navigation re-triggers bootstrap, exactly like a user
  // manually loading /app/ja/.... PathHelper resolves LOCALE.EN to the
  // canonical non-locale route automatically; the query string
  // (aai, uid, reportDebug=1, etc.) is preserved across the navigation.
  const handleDevLocaleChange = (targetLocale) => {
    const targetPath = PathHelper(APP_NAVIGATION.SOCIOPATH_TEST_REPORT, targetLocale).next;
    window.location.assign(`${targetPath}${location.search}`);
  };

  // Both read the already-computed classification band off reportData —
  // fixed color per band, no percentage-driven intensity (see
  // report-visual-style.js for why). gaugeVisualStyle paints the gauge;
  // classificationSeverityColors paints the label, Scores table row, and
  // classification card, so all three always agree.
  const gaugeVisualStyle = reportData
    ? getSeverityGaugeVisualStyle(reportData.overallResult.band)
    : null;
  const classificationSeverityColors = reportData
    ? getSeverityBandColors(reportData.overallResult.band)
    : null;

  // Guest-view fallback (unauth-only, no report content of its own). `data`
  // is accepted but never read — kept only so the call site's `({})` call
  // shape matches the equivalent block in other reports.
  const CallToActionSectionForUnAuth = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.personality.personality_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.personality.personality_type_learn_more")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.enneagram.enneagram_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.enneagram_self_awareness")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ENNEAGRAM_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.disc_personality.disc_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.disc_personality.take_assessment_cta")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${DISC_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto px-1 sm:px-4 py-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-screen w-full">
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <div className="max-w-5xl mx-auto space-y-8 pb-8">
              {/* SECTION 1 — Header + Overall Result */}
              <ExpandableSectionV2
                defaultExpanded={true}
                heroBanner={
                  <SectionBanner
                    title={reportData.header.title}
                    colors={BANNER_COLORS.header}
                  />
                }
              >
                <div>
                  {isAuth && (
                    // Two independent, content-sized pills (not a joined
                    // tray) — flex-wrap lets the whole second pill drop to
                    // its own row if it doesn't fit; ShareButton/
                    // SendToFriendButton's own "pill" classes (flex-shrink-0
                    // + whitespace-nowrap) keep each pill from shrinking or
                    // wrapping its label internally.
                    <div className="flex flex-wrap justify-end gap-1.5 sm:gap-[10px] mb-4">
                      <ShareButton
                        currentAssessmentAttemptData={assessmentResult}
                        userGUID={userGUID}
                        userData={userData}
                        variant="pill"
                        label={t("dashboard.share_report")}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={assessmentResult}
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={sendToFriendReportOnEmailConnect}
                        variant="pill"
                        label={t("dashboard.send_to_friend")}
                      />
                    </div>
                  )}
                  <p className="text-[15px] sm:text-base font-normal leading-relaxed text-gray-600 mb-5 max-w-3xl">
                    {reportData.header.intro}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="flex flex-col items-center">
                      <OverallResultGauge
                        score={reportData.overallResult.score}
                        maxScore={reportData.overallResult.maxScore}
                        label={reportData.overallResult.classification}
                        sublabel={`${t("report.sociopath.of")} ${reportData.overallResult.classificationsCount} ${t("report.sociopath.classifications")}`}
                        visualStyle={gaugeVisualStyle}
                      />
                      {/* The single, prominent statement of the overall
                          result — the gauge center shows the short label,
                          this is the one full classification label shown in
                          Section 1; the explanation card below no longer
                          repeats it (see the card's paragraphs-only body). */}
                      <div
                        className="text-center mt-2 font-extrabold text-xl sm:text-2xl break-words"
                        style={{ color: classificationSeverityColors.strong }}
                      >
                        {reportData.overallResult.classificationLabel}
                      </div>
                      <div className="mt-5 w-full">
                        <TraitSummaryList factors={reportData.factors} />
                      </div>
                    </div>
                    {/* Real asset is 1600x900 (16:9) — aspect-video sizes
                        the box to the actual image instead of a fixed
                        h-[320px]/h-[520px] guess, which left the image
                        letterboxed inside a lot of empty gray space at
                        every width (verified against the real PNG). Below
                        md: single column (grid-cols-1 above), so this box
                        isn't a stretched grid row partner and aspect-video
                        keeps it sized to its own content. This grid becomes
                        2-column at md: (grid grid-cols-1 md:grid-cols-2
                        above) — matching that same breakpoint here (not lg)
                        is what actually matters: from md: up, the grid row
                        stretches this column to the gauge/traits column's
                        height (CSS Grid default), so aspect-auto + h-full
                        lets the box use that full stretched height instead
                        of staying aspect-locked with empty space below it.
                        Verified via rendered screenshots at 821px (md, just
                        past the grid switch) — an earlier lg:-only version
                        of this fix left exactly that empty-space gap for
                        the whole 821-1023px range. */}
                    <div className="relative rounded-2xl overflow-hidden aspect-video w-full md:aspect-auto md:h-full bg-[#F5F5F5]">
                      <img
                        src={result1Image}
                        alt="Assessment Result"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-gray-800 mt-8 mb-3">
                    {reportData.overallResult.heading}
                  </h3>
                  {/* Both paragraphs (real backend content — nothing
                      invented) share the same body typography — no weight/
                      color distinction between them. Paragraph 2+ keeps its
                      top border as the only separation cue. */}
                  <div
                    className="border rounded-2xl p-6"
                    style={{
                      backgroundColor: classificationSeverityColors.bg,
                      borderColor: classificationSeverityColors.border,
                    }}
                  >
                    {reportData.overallResult.paragraphs.map((paragraph, index) => (
                      <p
                        key={index}
                        className={`${
                          index === 0 ? "" : "mt-3 pt-3 border-t border-black/5 "
                        }text-[15px] sm:text-base font-normal text-gray-600 leading-relaxed text-left`}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </ExpandableSectionV2>

              {/* SECTION 2 — Detailed Factor Analysis */}
              <ExpandableSectionV2
                heroBanner={
                  <SectionBanner
                    title={reportData.explanation.heading}
                    colors={BANNER_COLORS.explanation}
                  />
                }
              >
                <ExplanationCallout label={reportData.explanation.methodologyLabel}>
                  {renderMethodologyText(
                    reportData.explanation.methodologyText,
                    reportData.factors
                  )}
                </ExplanationCallout>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  {reportData.factors.map(({ key, ...factor }) => (
                    <FactorCard key={key} {...factor} />
                  ))}
                </div>
              </ExpandableSectionV2>

              {/* SECTION 3 — How to Use This Information */}
              <ExpandableSectionV2
                heroBanner={
                  <SectionBanner
                    title={reportData.insights.heading}
                    colors={BANNER_COLORS.insights}
                  />
                }
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <div className="flex flex-col gap-6">
                    <IconListCard
                      heading={reportData.insights.nextSteps.heading}
                      items={reportData.insights.nextSteps.items}
                    />
                    <NoticeCard
                      color="amber"
                      heading={reportData.insights.support.heading}
                      body={reportData.insights.support.body}
                    />
                  </div>
                  {/* Below lg: this column is single-width (grid-cols-1
                      applies up to the lg breakpoint here), so the box is
                      left to size to the image's own real aspect ratio
                      (1600x900) instead of a fixed min-h-[320px] — at wider
                      single-column widths (e.g. 768px) that fixed height was
                      shorter than the actual rendered image, so the
                      absolutely positioned img could get clipped by
                      overflow-hidden; a normal-flow image never can be.
                      At lg: and up the column stretches to match the text
                      column's height (grid default), so the original
                      min-h-[320px] + bottom-anchored absolute image is kept
                      exactly as before — desktop composition unchanged. */}
                  <div className="relative rounded-2xl overflow-hidden bg-[#F5F5F5] flex items-center justify-center lg:min-h-[320px]">
                    <img
                      src={result2Image}
                      alt=""
                      className="w-full h-auto p-2 lg:absolute lg:bottom-16 lg:left-1/2 lg:-translate-x-1/2 lg:p-8"
                    />
                  </div>
                </div>
                <NoticeCard
                  color="emerald"
                  heading={reportData.insights.limitations.heading}
                  body={reportData.insights.limitations.body}
                />
              </ExpandableSectionV2>

              {/* CTA / Invite — outside reportData, real shared components.
                  Same full report width as InviteFriend below (a narrower
                  wrapper here read as inconsistent sizing rather than
                  intentional hierarchy at ~1024px). Grouped into one
                  space-y-8 child so the parent's 32px section spacing only
                  applies once before this pair — the gap between the two
                  cards themselves comes solely from InviteFriend's own
                  sectionMode my-2 (~8px), matching Psychopathy/Social
                  Anxiety (see QA Round 3). */}
              <div>
                {isAuth ? (
                  <CallToActionSection
                    currentLocale={currentLocale}
                    t={t}
                    getAssessmentConnect={getAssessmentConnect}
                    getAssessmentAttemptByUserIdConnect={
                      getAssessmentAttemptByUserIdConnect
                    }
                    assessmentId={SOCIOPATH_TEST_ASSESSMENT_ID}
                    userId={userGUID}
                    bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                  />
                ) : (
                  CallToActionSectionForUnAuth({})
                )}

                {isAuth && (
                  <InviteFriend
                    sectionMode={true}
                    getGuestUserConnect={getGuestUserConnect}
                    getTransactionDetailConnect={getTransactionDetailConnect}
                    getOrderDataByUserIdConnect={getOrderDataByUserIdConnect}
                    getBillingConnect={getBillingConnect}
                    guestUserChangesConnect={guestUserChangesConnect}
                    isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                    userId={userGUID}
                    userEmail={userData?.email}
                    buttonText={t("dashboard.invite_friend_cta")}
                    selectedPlan={selectedPlan}
                    assessmentData={assessmentResult}
                    showRemainingInvites={true}
                    bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                  />
                )}
              </div>
            </div>
          )}
          <ReportWrapper
            assessmentId={SOCIOPATH_TEST_ASSESSMENT_ID}
            assessmentAttemptId={assessmentAttemptId}
            currentLocale={currentLocale}
          />
        </div>
      </div>
      {devPreviewAllowed && baseReportData && (
        <ReportDebugPanel
          overrides={devOverrides}
          onFactorChange={handleDevFactorChange}
          onOverallChange={handleDevOverallChange}
          onPreset={handleDevPreset}
          onReset={handleDevReset}
          currentLocale={currentLocale}
          onLocaleChange={handleDevLocaleChange}
        />
      )}
      <Footer />
    </>
  );
};

export default SociopathTestReport;
